import { useEffect, useState } from 'react'
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts'
import { RefreshCw } from 'lucide-react'
import { api } from '../services/api'

interface TreemapData {
  name: string
  value: number
  fill: string
}

export default function RevenueHeatmap() {
  const [data, setData] = useState<TreemapData[]>([])
  const [metric, setMetric] = useState<'revenue' | 'conversion_rate'>('revenue')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [autoRefresh, setAutoRefresh] = useState(true)

  const fetchData = async () => {
    setIsRefreshing(true)
    try {
      const result = await api.getHeatmapData(metric)
      if (Array.isArray(result)) {
        // 현대적이고 생생한 색상 팔레트
        const colorPalettes = {
          revenue: ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'],
          conversion_rate: ['#10b981', '#84cc16', '#eab308', '#f97316', '#ef4444']
        }
        
        const getColor = (index: number, total: number) => {
          const percentage = index / total
          const paletteIndex = Math.min(Math.floor(percentage * 5), 4)
          return metric === 'revenue'
            ? colorPalettes.revenue[paletteIndex]
            : colorPalettes.conversion_rate[4 - paletteIndex]
        }
        
        const treemapData: TreemapData[] = result.map((item, index) => ({
          name: item.platform,
          value: item.value,
          fill: getColor(index, result.length)
        }))
        
        setData(treemapData)
      }
    } catch (error) {
      console.error('Failed to fetch heatmap:', error)
    } finally {
      setIsRefreshing(false)
      setLastUpdated(new Date())
    }
  }

  useEffect(() => {
    fetchData()
  }, [metric])

  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(() => fetchData(), 30000)
    return () => clearInterval(interval)
  }, [metric, autoRefresh])

  return (
    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">
            {metric === 'revenue' ? '💰 매출 분포' : '📈 전환율 분포'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">16개 플랫폼 성과 현황</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as 'revenue' | 'conversion_rate')}
            className="bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 text-sm"
          >
            <option value="revenue">매출 ($)</option>
            <option value="conversion_rate">전환율 (%)</option>
          </select>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 rounded-lg text-sm transition-all ${
              autoRefresh
                ? 'bg-green-600 text-white'
                : 'bg-slate-700 text-gray-400'
            }`}
            title={autoRefresh ? '자동 새로고침 켜짐' : '자동 새로고침 꺼짐'}
          >
            {autoRefresh ? '🔄' : '⏸️'}
          </button>
          <button
            onClick={fetchData}
            disabled={isRefreshing}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <span className="text-xs text-gray-500">
            {lastUpdated.toLocaleTimeString('ko-KR')}
          </span>
        </div>
      </div>

      <div className="bg-slate-900/50 rounded-lg p-2 mb-4">
        <ResponsiveContainer width="100%" height={400}>
          <Treemap
            data={data}
            dataKey="value"
            stroke="#1e293b"
            fill="#8884d8"
            content={<CustomTreemapContent metric={metric} />}
          >
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                border: '1px solid #475569',
                borderRadius: '6px',
              }}
              formatter={(value: number) =>
                metric === 'revenue'
                  ? `$${value.toLocaleString()}`
                  : `${(value * 100).toFixed(1)}%`
              }
              labelStyle={{ color: '#e0e7ff' }}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-300 mb-2">범례:</p>
          <div className="space-y-1">
            <LegendItem color="bg-red-500" label="매우 높음" />
            <LegendItem color="bg-orange-500" label="높음" />
            <LegendItem color="bg-yellow-500" label="보통" />
            <LegendItem color="bg-lime-500" label="낮음" />
            <LegendItem color="bg-green-500" label="매우 낮음" />
          </div>
        </div>
        
        <div>
          <p className="text-sm font-semibold text-gray-300 mb-2">참고:</p>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• 박스 크기 = 데이터 규모</li>
            <li>• 색상 = 상대적 성과</li>
            <li>• 마우스 오버 = 상세</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center space-x-2">
      <div className={`w-3 h-3 rounded-sm ${color}`}></div>
      <span className="text-sm text-gray-400">{label}</span>
    </div>
  )
}

interface CustomTreemapContentProps {
  x?: number
  y?: number
  width?: number
  height?: number
  name?: string
  value?: number
  fill?: string
  metric?: string
}

const CustomTreemapContent = (props: CustomTreemapContentProps & { metric: string }) => {
  const { x = 0, y = 0, width = 0, height = 0, name, value, fill, metric } = props
  
  const getTextColor = (backgroundColor: string): string => {
    const hexColor = backgroundColor.replace('#', '')
    const r = parseInt(hexColor.substr(0, 2), 16)
    const g = parseInt(hexColor.substr(2, 2), 16)
    const b = parseInt(hexColor.substr(4, 2), 16)
    
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
    return luminance > 0.5 ? '#1f2937' : '#ffffff'
  }
  
  const textColor = getTextColor(fill || '#8884d8')
  const contrastColor = textColor === '#ffffff' ? '#000000' : '#ffffff'
  
  return (
    <g>
      <defs>
        <linearGradient id={`grad-${name}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={fill} stopOpacity={1} />
          <stop offset="100%" stopColor={fill} stopOpacity={0.85} />
        </linearGradient>
        <filter id={`shadow-${name}`}>
          <feDropShadow dx="0" dy="0" stdDeviation="3" floodOpacity="0.8" />
        </filter>
      </defs>
      
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: `url(#grad-${name})`,
          stroke: '#0f172a',
          strokeWidth: 2,
          opacity: 0.95,
          filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))',
        }}
      />
      
      <rect
        x={x + 1}
        y={y + 1}
        width={width - 2}
        height={1}
        style={{ fill, opacity: 0.5 }}
      />
      
      {width > 50 && height > 40 && (
        <>
          {/* 배경 박스 */}
          <rect
            x={x + width / 2 - 45}
            y={y + height / 2 - 30}
            width={90}
            height={58}
            rx={4}
            style={{
              fill: 'rgba(0, 0, 0, 0.3)',
              pointerEvents: 'none',
            }}
          />
          
          {/* 플랫폼 이름 */}
          <text
            x={x + width / 2}
            y={y + height / 2 - 14}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={contrastColor}
            fontSize={3}
            fontWeight="700"
            style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              WebkitFontSmoothing: 'antialiased',
              textRendering: 'optimizeLegibility',
              pointerEvents: 'none',
              userSelect: 'none',
              paintOrder: 'stroke',
              stroke: textColor,
              strokeWidth: 0.3,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          >
            {name}
          </text>
          
          {/* 값 */}
          <text
            x={x + width / 2}
            y={y + height / 2 + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={textColor}
            fontSize={3.5}
            fontWeight="800"
            style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              WebkitFontSmoothing: 'antialiased',
              textRendering: 'optimizeLegibility',
              pointerEvents: 'none',
              userSelect: 'none',
              paintOrder: 'stroke',
              stroke: contrastColor,
              strokeWidth: 0.2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          >
            {metric === 'revenue'
              ? `$${(value || 0).toLocaleString()}`
              : `${((value || 0) * 100).toFixed(1)}%`}
          </text>
        </>
      )}
    </g>
  )
}
